<template>
  <div class="default-page-layout flex-col gap-10">
    <div :class="['flex-col gap-8', props.centered ? 'align-center' : '']">
      <template v-if="$slots['banner']">
        <slot name="banner" />
      </template>

      <PageHeader
        v-if="
          hideHeader
            ? false
            : computedTitle || computedSubtitle || $slots['actions']
        "
        :title="computedTitle"
        :subtitle="computedSubtitle"
        :class="props.centered ? 'page-header-centered' : ''"
      >
        <template v-if="subtitle || $slots['subtitle']" #subtitle>
          <slot name="subtitle" />
        </template>

        <template v-if="$slots['actions']" #actions>
          <slot name="actions" />
        </template>
      </PageHeader>

      <div v-if="$slots['filter-bar']" class="flex-col gap-10">
        <div class="flex-row gap-3 align-center justify-start">
          <slot name="filter-bar" />
        </div>
      </div>
    </div>

    <CollectionsBanner v-if="activeBrandId" />

    <div v-if="props.centered" class="flex-row justify-center w-full">
      <slot />
    </div>
    <slot v-else />
  </div>

  <slot name="dialog" />
</template>

<script lang="ts" setup>
import { storeToRefs } from 'pinia'
import { computed } from 'vue'

import { usePageDescription } from '@/core/nav/utils/usePageDescription'
import { usePageTitle } from '@/core/nav/utils/usePageTitle'
import CollectionsBanner from '@/core/public/CollectionsBanner.vue'
import { useCoreStore } from '@/core/store/useCoreStore'
import { useBreakpoints } from '@/utils/useBreakpoints'

import PageHeader from './PageHeader.vue'

const props = withDefaults(
  defineProps<{
    centered?: boolean
    hideHeader?: boolean
    maxWidth?: number
    subtitle?: string
    title?: string
  }>(),
  {
    centered: false,
    hideHeader: false,
    maxWidth: 1440,
    subtitle: undefined,
    title: undefined,
  }
)

const { isDesktop } = useBreakpoints()
const { activeBrandId } = storeToRefs(useCoreStore())

const maxWidth = computed(() => `${props.maxWidth + 80}px`)

const pageTitle = usePageTitle()
const pageDescription = usePageDescription()

const computedTitle = computed(() => {
  if (props.title) return props.title

  if (!isDesktop.value) return undefined
  return pageTitle.value
})

const computedSubtitle = computed(() => {
  if (props.subtitle) return props.subtitle

  return pageDescription.value
})
</script>

<style lang="scss" scoped>
@use '@/styles/breakpoints';

.default-page-layout {
  background-color: var(--background-primary);
  max-width: v-bind(maxWidth);
  min-height: 100%;
  padding: 40px 16px;

  @include breakpoints.respond-to('md-and-up') {
    margin: 0 auto;
    padding: 40px 56px;
  }
}

.page-header-centered {
  width: 100%;
  max-width: 1200px;

  @include breakpoints.respond-to('lg-and-up') {
    width: 50%;
  }
}
</style>
